/* oxlint-disable @typescript-eslint/no-explicit-any */
import type { SeverityLevel } from '@sentry/browser';
import type { GraphQLError, GraphQLFormattedError } from 'graphql';

import type WrappedError from './errors/WrappedError/WrappedError';

export enum SentryEnvironments {
  PRODUCTION = 'production',
  STAGING = 'staging',
  DEVELOPMENT = 'development',
}

export type { GraphQLError };

export type ErrorStack = {
  componentStack: string;
};

export type ErrorToastConfig = {
  title?: string;
  description?: string;
  variant?: 'danger' | 'success' | 'warning' | 'alert' | 'info' | 'loading';
  [key: string]: unknown;
};

export type ErrorOptions = {
  neverThrowError?: boolean;
  orgID?: string;
  operationName?: string;
  operationVariables?: Record<string, any>;
  toastConfig?: ErrorToastConfig;
  // Additional arbitrary context to send to Sentry
  extraContext?: Record<string, unknown>;
  // Skip the Sentry capture for this error. For operations whose errors are
  // expected and non-actionable (e.g. a permission probe the BE answers with
  // an error). Orthogonal to the toast — pair with the link-level `skipToast`
  // context flag to fully silence an error.
  skipErrorReport?: boolean;
  // Override the default 'error' severity, e.g. 'log' for expected noise
  // that's still worth keeping in Sentry for visibility
  level?: SeverityLevel;
};

export type ServerError = Error & {
  response: Response;
  result: any;
  statusCode: number;
};

// Thrown when a server's resonse is cannot be parsed
export type ServerParseError = Error & {
  response: Response;
  statusCode: number;
  bodyText: string;
};

// Error that can be thrown inside the Apollo lifecycle
// https://github.com/apollographql/apollo-link/blob/master/packages/apollo-link-error/src/index.ts#L13-L19
export type ApolloLinkError = {
  graphQLErrors?: GraphQLError[];
  networkError?: Error | ServerError | ServerParseError;
};

export type ClientError =
  | Error
  | WrappedError
  | GraphQLError
  | GraphQLFormattedError;
